import { useState, useEffect } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Badge, Popover, Typography } from 'antd';
import moment from 'moment';
import { countries, languages } from 'countries-list';
import REWIREskillSelectionModalForm from '../../admin/modals/REWIREskillSelectionModalForm.js';

const { TextArea } = Input;
const { Title } = Typography;

const TrainingCSPModalForm = ({ editRecord, refresh, onCancel, rsSkillsGroup, rsSpecificKnowledge, rsSpecificSkills, dbTrainings, saveLocally }) => {
  const [modalLoading, setModalLoading] = useState(false);
  const [form] = Form.useForm();
  const [isNew, setIsNew] = useState(false);
  const [formDates, setFormDates] = useState([]);
  const [skillModalShown, setSkillModalShown] = useState(false);
  const [skillFromData, setSkillFromData] = useState();

  useEffect(() => {
    if (typeof editRecord === 'object' && editRecord !== null && form.getFieldValue('id') !== editRecord.id) {
      let initialData = JSON.parse(JSON.stringify(editRecord));
      delete initialData.key;
      form.setFieldsValue(initialData);
      setFormDates(initialData.dates ? initialData.dates : []);
      setIsNew(false);
      let initialSkillFormData = {
        skills_group: editRecord.skills_group || [],
        specific_skills: editRecord.specific_skills || [],
        specific_knowledge: editRecord.specific_knowledge || [],
      };
      setSkillFromData(initialSkillFormData);
    } else if (editRecord === 'new') {
      setIsNew(true);
      setFormDates([]);
      setSkillFromData({
        skills_group: [],
        specific_skills: [],
        specific_knowledge: [],
      });
    }
  }, [editRecord, form]);
  function nameExists(value) {
    return dbTrainings.find((e) => e.name === value) ? true : false;
  }

  const onDateChange = (date, index) => {
    const updatedDates = [...formDates];
    updatedDates[index] = date ? date.format('YYYY-MM-DD') : null;
    setFormDates(updatedDates);
    form.setFieldsValue({ dates: updatedDates });
  };

  const removeDatePicker = (index) => {
    const updatedDates = formDates.filter((_, i) => i !== index);
    setFormDates(updatedDates);
    form.setFieldsValue({ dates: updatedDates });
  };

  const addDatePicker = () => {
    setFormDates([...formDates, null]);
  };
  return (
    <Modal
      getContainer={false}
      centered
      open={editRecord !== null}
      title={isNew ? 'Add a new training' : 'Edit training'}
      className={isNew ? 'add-entity-modal' : 'edit-entity-modal'}
      width={850}
      okText="Save"
      confirmLoading={modalLoading}
      cancelText="Cancel"
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form.validateFields().then((values) => {
          setModalLoading(true);

          values.skills_group = skillFromData.skills_group || [];

          const formData = {
            ...values,
            created: values.created ? values.created : moment().format('YYYY-MM-DD HH:mm:ss'),
            updated: moment().format('YYYY-MM-DD HH:mm:ss'),
          };
          saveLocally(formData);
          setModalLoading(false);
          refresh();
        });
      }}
    >
      <Form
        form={form}
        layout="horizontal"
        name={isNew ? 'add-csp-training-modal' : 'edit-csp-training-modal'}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 15 }}
        labelWrap
        colon={false}
        className="content-scroll"
      >
        <Form.Item
          name="name"
          label="Training name"
          rules={[
            { required: true, message: 'Please enter the title' },
            { type: 'string', min: 2, max: 1023 },
            () => ({
              validator(_, value) {
                if (!value || !nameExists(value) || !isNew) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('This training is already stored in the database!'));
              },
            }),
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="organizer"
          label="Organizer"
          rules={[
            {
              required: true,
              message: 'Please enter the organizer',
            },
            { type: 'string', min: 2, max: 1023 },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[
            {
              required: true,
              message: 'Please enter the description',
            },
            { type: 'string', min: 2, max: 1023 },
          ]}
        >
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item name="country" label="Country" rules={[{ required: true, message: 'Please select country.' }]}>
          <Select showSearch showArrow allowClear>
            {Object.values(countries).map((country, index) => (
              <Select.Option key={index} value={country.name}>
                {country.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="duration" label="Duration (hours)" rules={[{ required: true, message: 'Please enter the duration' }]}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="type_format"
          label="Format"
          rules={[
            {
              type: 'string',
              required: true,
              message: 'Please select one option',
            },
          ]}
        >
          <Select showSearch showArrow allowClear>
            <Select.Option value="Face-to-face" label="Face-to-face" />
            <Select.Option value="Online" label="Online" />
            <Select.Option value="Hybrid" label="Hybrid" />
          </Select>
        </Form.Item>
        <Form.Item
          name="timing"
          label="Timing"
          rules={[
            {
              type: 'string',
              required: true,
              message: 'Please select one option',
            },
          ]}
        >
          <Select showSearch showArrow allowClear>
            <Select.Option value="Fixed dates" label="Fixed dates" />
            <Select.Option value="Available online" label="Available online" />
            <Select.Option value="On demand" label="On demand" />
          </Select>
        </Form.Item>
        <Form.Item
          name="dates"
          label="Dates"
          dependencies={['timing']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (getFieldValue('timing') === 'Fixed dates') {
                  if (!value || !Array.isArray(value) || value.length === 0 || value.every((date) => date === null || date === undefined)) {
                    return Promise.reject(new Error('Please add at least one date.'));
                  }
                  if (value.some((date) => date === null || date === undefined)) {
                    return Promise.reject(new Error('All date fields must be filled.'));
                  }
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Space direction="vertical">
            {formDates.map((date, index) => (
              <Space key={index} align="baseline">
                <DatePicker value={date ? moment(date, 'YYYY-MM-DD') : null} onChange={(date) => onDateChange(date, index)} />
                <Button onClick={() => removeDatePicker(index)}>Remove</Button>
              </Space>
            ))}
            <Button type="dashed" onClick={addDatePicker}>
              Add Date
            </Button>
          </Space>
        </Form.Item>
        <Form.Item
          name="content_type"
          label="Content type"
          rules={[
            {
              type: 'string',
              required: true,
              message: 'Please select one option',
            },
          ]}
        >
          <Select showSearch showArrow allowClear>
            <Select.Option value="Theoretical only" label="Theoretical only" />
            <Select.Option value="Hands on" label="Hands on" />
            <Select.Option value="Theoretical and hands on" label="Theoretical and hands on" />
          </Select>
        </Form.Item>
        <Form.Item name="prerequisites" label="Prerequisites" rules={[{ type: 'string', max: 1023 }]}>
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item
          name="can_lead_to_certification"
          label="Can lead to certification"
          rules={[
            {
              type: 'string',
              required: true,
              message: 'Please select one option',
            },
            { type: 'string', min: 2, max: 1023 },
          ]}
        >
          <Select showSearch showArrow allowClear>
            <Select.Option
              value="Yes (based on specific standarts, recognized by national/internation organizations)"
              label="Yes (based on specific standarts, recognized by national/internation organizations)"
            />
            <Select.Option value="No" label="No" />
            <Select.Option value="Other (eg. certificate of attendance / participation)" label="Other (eg. certificate of attendance / participation)" />
          </Select>
        </Form.Item>
        <Form.Item
          name="includes_exams_for_certification"
          label="Includes exams for certification"
          rules={[
            {
              type: 'string',
              required: true,
              message: 'Please select one option',
            },
            { type: 'string', min: 2, max: 1023 },
          ]}
        >
          <Select showSearch showArrow allowClear>
            <Select.Option value="Yes" label="Yes" />
            <Select.Option value="No" label="No" />
          </Select>
        </Form.Item>
        <Form.Item name="language" label="Language" rules={[{ required: true, message: 'Please select at least one language.' }]}>
          <Select showSearch showArrow allowClear mode="multiple">
            {Object.values(languages).map((language, index) => (
              <Select.Option key={index} value={language.name}>
                {language.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="price"
          label="Price"
          rules={[
            { required: true, message: 'Please provide price.' },
            { type: 'int', min: 0 },
          ]}
        >
          <InputNumber addonAfter="€" min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="skills_group" label="REWIRE Skills">
          <Space direction="vertical" size={16}>
            <Space wrap className="skillBadges">
              {skillFromData?.skills_group.map((skillGroup) => {
                const grp = rsSkillsGroup.find((g) => g.id === skillGroup);
                return (
                  <Popover
                    getPopupContainer={() => document.body}
                    overlayStyle={{
                      position: 'fixed',
                      overflow: 'visible',
                    }}
                    key={grp.id}
                    content={
                      <div className="skills-popover">
                        <Title level={4}>{grp.name}</Title>
                        <Title level={5}>Included Skills</Title>
                        <ul>
                          {grp.specific_skills.length > 0 ? (
                            grp.specific_skills.map((sg, i) => <li key={i}>{rsSpecificSkills.find((e) => e.id === sg)?.name}</li>)
                          ) : (
                            <span className="empty">None</span>
                          )}
                        </ul>
                        <Title level={5}>Included Knowledge</Title>
                        <ul>
                          {grp.specific_knowledge.length > 0 ? (
                            grp.specific_knowledge.map((sg, i) => <li key={i}>{rsSpecificKnowledge.find((e) => e.id === sg)?.name}</li>)
                          ) : (
                            <span className="empty">None</span>
                          )}
                        </ul>
                      </div>
                    }
                  >
                    <Badge count={grp.name} color={grp.color || 'white'} key={skillGroup} className="skillBadge" />
                  </Popover>
                );
              })}
            </Space>
            <Button onClick={() => setSkillModalShown(true)}>Choose</Button>
          </Space>
        </Form.Item>
        <Form.Item name="link" label="Link" rules={[{ type: 'string', required: true, message: 'Please enter link.' }]}>
          <Input />
        </Form.Item>

        {typeof editRecord === 'object' && editRecord !== null ? (
          <>
            <Form.Item label="Created">{editRecord.created} [CET]</Form.Item>

            <Form.Item label="Updated">{editRecord.updated} [CET]</Form.Item>
          </>
        ) : null}

        <Form.Item name="id" label="Id" hidden>
          <Input />
        </Form.Item>
      </Form>
      <REWIREskillSelectionModalForm
        skillModalShown={skillModalShown}
        initialData={skillFromData}
        onCancel={() => setSkillModalShown(false)}
        saveFormData={(data) => setSkillFromData(data)}
        rsSkillsGroup={rsSkillsGroup}
        rsSpecificKnowledge={rsSpecificKnowledge}
        rsSpecificSkills={rsSpecificSkills}
      />
    </Modal>
  );
};

export default TrainingCSPModalForm;
